import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    Radio,
    RadioGroup,
    Typography,
} from "@mui/material";

import accountActions from "../../actions/accountActions";
import oauthActions from "../../actions/oauthActions";
import { getMfaMethods } from "../../reducers";

const SecuritySettingsDialog = ({ open, onClose, data = {}, ...others }) => {
    const dispatch = useDispatch();

    useEffect(() => {
        oauthActions.getMfaMethods()(dispatch);
    }, [dispatch]);

    const [isEnabled, setIsEnabled] = useState(!!data?.MFA?.isEnabled);
    const [method, setMethod] = useState(data?.MFA?.method?.id || null);

    const mfaMethods = useSelector(getMfaMethods) || [];

    const onSave = () => {
        const params = {
            MFA: {
                isEnabled,
                method: mfaMethods.find(
                    ({ id }) => Number(id) === Number(method)
                ),
            },
        };
        dispatch(accountActions.editSecuritySettings(params));
    };

    return (
        <Dialog open={open} onClose={onClose} {...others}>
            <DialogTitle>Multifactor authentication (MFA)</DialogTitle>
            <DialogContent>
                <Box
                    component="form"
                    autoComplete="off"
                    noValidate
                    className="dialog-content"
                >
                    <Typography variant="body1" paragraph>
                        For enhanced security, we recommend enabling
                        Multi-Factor Authentication (MFA). This adds an extra
                        layer of protection by requiring both your password and
                        a secondary verification method.
                    </Typography>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="mfa_enabled"
                                    checked={isEnabled}
                                    onChange={({ target }) =>
                                        setIsEnabled(target.checked)
                                    }
                                />
                            }
                            label="Enable Multi-Factor Authentication"
                        />
                    </FormGroup>

                    {isEnabled && (
                        <FormControl component="fieldset">
                            <Typography variant="body1" paragraph>
                                Choose your preferred verification method:
                            </Typography>
                            <RadioGroup
                                row
                                name="radio-mfa-method"
                                aria-label="mfa-method"
                                value={method}
                                onChange={({ target }) =>
                                    setMethod(target.value)
                                }
                            >
                                {mfaMethods.map(({ id, type }) => (
                                    <FormControlLabel
                                        key={id}
                                        value={id}
                                        control={<Radio />}
                                        label={type}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    key="save"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        onSave();
                        onClose();
                    }}
                    disabled={isEnabled && !method}
                    disableElevation={true}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

SecuritySettingsDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
};

SecuritySettingsDialog.defaultProps = {
    open: false,
    onClose: () => {},
};

export default SecuritySettingsDialog;
